/**
 * R4 — Onglet « Réception » du chantier (RP47 à RP50, D21 = B, D34), complété par
 * Fluidité B4 (fin de chantier : P5#4-#5, #9-#20, B15).
 *
 * - Maîtrise d'œuvre : visite de réception (OPR) avec les présents à cocher, alerte si
 *   un lot figure déjà dans une autre réception et récapitulatif de toutes les
 *   réceptions ; réserves par lot (photo, localisation, délai), relance de l'entreprise ;
 *   levée validée ou refusée (motif dans un vrai formulaire) ; PV avec ou sans réserves
 *   (date d'effet au calendrier), signé par code (client et entreprises des lots) ou
 *   « PV signé sur papier » (date + scan) ; puis GPA, après réception (DOE, retenue,
 *   DGD) et clôture du chantier.
 * - Client : état des réserves, PV, fin de la garantie et « Signaler un désordre ».
 * - Entreprise : les réserves et demandes en GPA de ses lots ; la levée se fait depuis
 *   l'app terrain (« Fait, avec photo »).
 * API : backend/routes/reception_routes.py, fin_chantier_routes.py, signatures_routes.py.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, FilePdf, CheckCircle, XCircle, Camera, MapPin, Flag, PaperPlaneTilt, Warning, Signature } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import usePermissions from '../../hooks/usePermissions';
import SignatureManager from './SignatureManager';
import DemandesGPA from './reception/DemandesGPA';
import ApresReception from './reception/ApresReception';
import ClotureChantier from './reception/ClotureChantier';
import { OBJETS, BOUTONS, FONCTIONS, STATUT_RESERVE } from '../../lib/libelles';
import { libelleRelance, toastEnvoi } from '../../lib/envoiEmail';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MOE = ['archi', 'moe_principal', 'moe_membre'];
const TITRE_VISITE = 'Visite de réception (OPR)';

const COULEUR_STATUT = {
  ouverte: 'bg-[#FFF8E1] text-[#7A5A00] border-[#B8860B]',
  a_verifier: 'bg-[#E3F2FD] text-[#1E3A5F] border-[#1E3A5F]',
  levee: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]',
};

const STATUT_PV = {
  genere: 'À signer',
  en_signature: 'Signature en cours',
  signe: 'Signé',
  remplace: 'Remplacé',
};

const dateFr = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).length <= 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('fr-FR');
};

const aujourdHui = () => new Date().toISOString().slice(0, 10);

const telechargerBlob = (data, nom) => {
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  a.click();
  URL.revokeObjectURL(url);
};

export default function ProjectReception({ chantierId }) {
  const { role_projet } = usePermissions(chantierId);
  const isMoe = MOE.includes(role_projet);
  const isClient = role_projet === 'client' || role_projet === 'amo';
  const isEntreprise = role_projet === 'entreprise';

  const [receptions, setReceptions] = useState([]);
  const [selId, setSelId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ouvrirOpen, setOuvrirOpen] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [pvOpen, setPvOpen] = useState(false);
  const [papierOpen, setPapierOpen] = useState(false);
  const [refus, setRefus] = useState(null); // { item, apres }
  const [refreshKey, setRefreshKey] = useState(0);

  const charger = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/receptions`, { withCredentials: true });
      const liste = data || [];
      setReceptions(liste);
      // P5#15 — par défaut, la réception qui a encore des réserves à suivre (sinon la plus récente)
      const aSuivre = liste.find((r) => (r.totaux?.ouvertes || 0) + (r.totaux?.a_verifier || 0) > 0);
      setSelId((cur) => (cur && liste.some((r) => r.id === cur) ? cur : (aSuivre || liste[0])?.id || null));
    } catch {
      setReceptions([]);
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  const chargerDetail = useCallback(async () => {
    if (!selId) { setDetail(null); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/receptions/${selId}`, { withCredentials: true });
      setDetail(data);
    } catch {
      setDetail(null);
    }
  }, [chantierId, selId]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { chargerDetail(); }, [chargerDetail]);

  const rafraichir = () => { charger(); chargerDetail(); setRefreshKey((k) => k + 1); };

  const telechargerPv = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/receptions/${selId}/pv.pdf`,
        { withCredentials: true, responseType: 'blob' });
      telechargerBlob(res.data, `${OBJETS.pvReception} ${detail?.pv?.date_effet || ''}.pdf`.trim());
    } catch {
      toast.error('Téléchargement impossible');
    }
  };

  // Levée d'une réserve ou d'une demande en GPA : le refus passe par un vrai formulaire (P5#12)
  const decisionLevee = async (item, valider, apres) => {
    if (!valider) { setRefus({ item, apres }); return; }
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/elements/${item.id}/valider-levee`, {}, { withCredentials: true });
      toast.success('Levée validée');
      if (apres) apres(); else rafraichir();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const relancerLot = async (lot) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/receptions/${selId}/relancer`, { lot }, { withCredentials: true });
      toastEnvoi(toast, data);
      chargerDetail();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Relance impossible');
    }
  };

  const reservesParLot = useMemo(() => {
    const m = {};
    (detail?.reserves || []).forEach((r) => { (m[r.lot] = m[r.lot] || []).push(r); });
    return m;
  }, [detail]);

  const derniereRelance = (lot) => (detail?.relances || []).filter((x) => x.lot === lot).slice(-1)[0];

  const signatairesProposes = useMemo(() => [
    ...(detail?.signataires_proposes || []).map((s) => ({ ...s, role: "Maître d'ouvrage" })),
    ...(detail?.signataires_entreprises || []).map((s) => ({ nom: s.nom, email: s.email, telephone: s.telephone, role: s.role })),
  ], [detail]);

  if (loading) return <p className="text-sm text-[#6B6B6B]">Chargement…</p>;

  return (
    <div className="space-y-6" data-testid="project-reception">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>{OBJETS.receptionTravaux}</h2>
          <p className="text-sm text-[#6B6B6B]">
            {isClient
              ? 'Les réserves sont les défauts à corriger relevés à la réception. Vous êtes prévenu quand toutes les réserves d\'un lot sont levées.'
              : 'Visite de réception (OPR), réserves par lot, PV, puis garantie de parfait achèvement et clôture.'}
          </p>
        </div>
        {isMoe && (
          <Button onClick={() => setOuvrirOpen(true)} className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none gap-2" data-testid="reception-ouvrir">
            <Plus size={16} weight="bold" /> Nouvelle visite de réception (OPR)
          </Button>
        )}
      </div>

      {receptions.length === 0 ? (
        <div className="border border-dashed border-[#E0E0DB] p-8 text-center text-sm text-[#6B6B6B]" data-testid="reception-vide">
          Aucune réception pour ce chantier.
          {isMoe && ' Ouvrez une visite de réception (OPR) pour saisir les réserves, ici ou sur l\'iPad (app terrain, Plus › Réception).'}
        </div>
      ) : (
        <>
          {/* P5#15 — récapitulatif de toutes les réceptions */}
          <div className="bg-white border border-[#E0E0DB]" data-testid="reception-recap">
            <div className="px-3 py-2 bg-[#FAFAF8] border-b border-[#E0E0DB] text-sm font-medium">
              {receptions.length > 1 ? `Toutes les réceptions (${receptions.length})` : 'Réception'}
            </div>
            <table className="w-full text-sm">
              <thead className="text-[#6B6B6B] text-xs">
                <tr>
                  <th className="text-left p-2 font-normal">Visite du</th>
                  <th className="text-left p-2 font-normal">Lots</th>
                  <th className="text-right p-2 font-normal">{STATUT_RESERVE.ouverte}s</th>
                  <th className="text-right p-2 font-normal">{STATUT_RESERVE.a_verifier}</th>
                  <th className="text-right p-2 font-normal">{STATUT_RESERVE.levee}s</th>
                  <th className="text-left p-2 font-normal">PV</th>
                </tr>
              </thead>
              <tbody>
                {receptions.map((r) => (
                  <tr key={r.id} onClick={() => setSelId(r.id)} data-testid={`reception-recap-${r.id}`}
                    className={`border-t border-[#F0F0EB] cursor-pointer ${r.id === selId ? 'bg-[#FFF5EB]' : 'hover:bg-[#FAFAF8]'}`}>
                    <td className="p-2">{dateFr(r.date_reception)}</td>
                    <td className="p-2">{(r.lots || []).map((l) => l.code).join(', ')}</td>
                    <td className="p-2 text-right">{r.totaux?.ouvertes || 0}</td>
                    <td className="p-2 text-right">{r.totaux?.a_verifier || 0}</td>
                    <td className="p-2 text-right">{r.totaux?.levees || 0}</td>
                    <td className="p-2">{r.pv ? STATUT_PV[r.pv.statut] || '' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {detail && (
            <>
              <div className="bg-white border border-[#E0E0DB] p-4 space-y-1" data-testid="reception-entete">
                <p className="font-medium text-[#2C2C2C]">{TITRE_VISITE} du {dateFr(detail.date_reception)}</p>
                <p className="text-sm text-[#6B6B6B]">
                  Lots : {(detail.lots || []).map((l) => `${l.code}${l.nom && l.nom !== l.code ? ` ${l.nom}` : ''}`).join(', ')}
                </p>
                {detail.presents?.length > 0 && (
                  <p className="text-sm text-[#6B6B6B]">
                    Présents : {detail.presents.map((p) => (p.qualite ? `${p.nom} (${p.qualite})` : p.nom)).join(', ')}
                  </p>
                )}
                {(detail.lots_autres_receptions || []).length > 0 && (
                  <p className="text-xs text-[#8B5A00] flex items-start gap-1" data-testid="reception-lot-ailleurs">
                    <Warning size={14} className="shrink-0 mt-0.5" />
                    {detail.lots_autres_receptions.map((x) => `Le lot ${x.code} figure aussi dans la réception du ${dateFr(x.date_reception)}`).join(' · ')} : voir le récapitulatif ci-dessus.
                  </p>
                )}
              </div>

              {/* Tableau par lot (RP50) */}
              <div className="bg-white border border-[#E0E0DB]" data-testid="reception-tableau">
                <table className="w-full text-sm">
                  <thead className="bg-[#F1EFEA] text-[#4A4A4A]">
                    <tr>
                      <th className="text-left p-2">Lot</th>
                      <th className="text-left p-2">Entreprise</th>
                      <th className="text-right p-2">{STATUT_RESERVE.ouverte}s</th>
                      <th className="text-right p-2">{STATUT_RESERVE.a_verifier}</th>
                      <th className="text-right p-2">{STATUT_RESERVE.levee}s</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.tableau || []).map((l) => (
                      <tr key={l.lot} className="border-t border-[#F0F0EB]" data-testid={`reception-tableau-${l.lot}`}>
                        <td className="p-2">{l.lot}{l.lot_nom && l.lot_nom !== l.lot ? ` — ${l.lot_nom}` : ''}</td>
                        <td className="p-2 text-[#6B6B6B]">{l.entreprise}</td>
                        <td className="p-2 text-right">{l.ouvertes}</td>
                        <td className="p-2 text-right">{l.a_verifier}</td>
                        <td className="p-2 text-right">
                          {l.levees}
                          {l.toutes_levees && <Badge className="ml-2 rounded-none bg-[#E8F5E9] text-[#2D6A4F] border border-[#2D6A4F]">Toutes levées</Badge>}
                          {l.sans_reserve && <span className="ml-2 text-xs text-[#6B6B6B]">Sans réserve</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Réserves par lot */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-[#2C2C2C]">{OBJETS.reserves}</h3>
                  {isMoe && detail.statut !== 'pv_signe' && (
                    <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => setReserveOpen(true)} data-testid="reception-ajouter-reserve">
                      <Plus size={14} /> Ajouter une réserve
                    </Button>
                  )}
                </div>
                {(detail.reserves || []).length === 0 && (
                  <p className="text-sm text-[#6B6B6B] italic">Aucune réserve : la réception peut être prononcée sans réserve.</p>
                )}
                {Object.entries(reservesParLot).map(([lot, rs]) => {
                  const nbOuvertes = rs.filter((r) => r.statut === 'ouverte').length;
                  const relance = libelleRelance(derniereRelance(lot), dateFr);
                  return (
                    <div key={lot} className="bg-white border border-[#E0E0DB]">
                      <div className="px-3 py-2 bg-[#FAFAF8] border-b border-[#E0E0DB] text-sm font-medium flex flex-wrap items-center gap-2">
                        <span>Lot {lot}{rs[0]?.lot_nom && rs[0].lot_nom !== lot ? ` — ${rs[0].lot_nom}` : ''} · {rs[0]?.entreprise}</span>
                        {isMoe && nbOuvertes > 0 && (
                          <span className="ml-auto flex items-center gap-2 font-normal">
                            {relance && <span className={`text-xs ${relance.envoye ? 'text-[#6B6B6B]' : 'text-[#8B3A3A]'}`} data-testid={`reception-relance-etat-${lot}`}>{relance.texte}</span>}
                            <Button size="sm" variant="outline" className="h-7 rounded-none gap-1 text-xs" onClick={() => relancerLot(lot)} data-testid={`reception-relancer-${lot}`}>
                              <PaperPlaneTilt size={12} /> Relancer l'entreprise
                            </Button>
                          </span>
                        )}
                      </div>
                      {rs.map((r) => (
                        <div key={r.id} className="px-3 py-2 border-b border-[#F0F0EB] last:border-b-0 flex gap-3 items-start" data-testid={`reception-reserve-${r.id}`}>
                          {r.photo_url
                            ? <a href={r.photo_url} target="_blank" rel="noreferrer"><img src={r.photo_url} alt="" className="w-14 h-14 object-cover border border-[#E0E0DB]" /></a>
                            : <div className="w-14 h-14 bg-[#F5F5F3] border border-[#E0E0DB]" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[#2C2C2C]">{r.description}</p>
                            <p className="text-xs text-[#6B6B6B] flex flex-wrap gap-x-3">
                              {r.localisation && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {r.localisation}</span>}
                              {r.echeance && <span>À lever avant le {dateFr(r.echeance)}</span>}
                            </p>
                            {r.statut === 'a_verifier' && r.derniere_preuve && (
                              <p className="text-xs text-[#1E3A5F] mt-1">
                                Preuve de {r.derniere_preuve.by_user_label || "l'entreprise"}
                                {r.derniere_preuve.commentaire ? ` : ${r.derniere_preuve.commentaire}` : ''}
                                {r.derniere_preuve.photo_url && <> · <a className="underline" href={r.derniere_preuve.photo_url} target="_blank" rel="noreferrer">photo</a></>}
                              </p>
                            )}
                            {r.levee_refusee_motif && <p className="text-xs text-[#8B3A3A] mt-1">Levée refusée : {r.levee_refusee_motif}</p>}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge className={`rounded-none border ${COULEUR_STATUT[r.statut] || ''}`}>{r.statut_libelle}</Badge>
                            {isMoe && r.statut === 'a_verifier' && (
                              <div className="flex gap-1">
                                <Button size="sm" className="h-7 rounded-none bg-[#2D6A4F] hover:bg-[#1B4332] gap-1 text-xs" onClick={() => decisionLevee(r, true)} data-testid={`reception-valider-${r.id}`}>
                                  <CheckCircle size={12} /> {BOUTONS.validerLevee}
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 rounded-none gap-1 text-xs text-[#8B3A3A]" onClick={() => decisionLevee(r, false)} data-testid={`reception-refuser-${r.id}`}>
                                  <XCircle size={12} /> {BOUTONS.refuserLevee}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {isEntreprise && (detail.reserves || []).some((r) => r.statut === 'ouverte') && (
                  <p className="text-xs text-[#6B6B6B]">
                    Pour lever une réserve, utilisez « {BOUTONS.faitAvecPhoto} » dans l'app terrain : l'architecte vérifiera ensuite la levée.
                  </p>
                )}
              </div>

              {/* PV de réception (RP49) */}
              <div className="bg-white border border-[#E0E0DB] p-4 space-y-3" data-testid="reception-pv">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Flag size={18} className="text-[#C4773B]" />
                    <h3 className="font-medium text-[#2C2C2C]">{OBJETS.pvReception}</h3>
                    {detail.pv && (
                      <Badge className="rounded-none bg-[#F5F5F3] text-[#4A4A4A] border border-[#E0E0DB]">
                        {STATUT_PV[detail.pv.statut] || ''}{detail.pv.statut === 'signe' && detail.pv.mode_signature === 'papier' ? ' sur papier' : ''}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {detail.pv && (
                      <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={telechargerPv} data-testid="reception-pv-telecharger">
                        <FilePdf size={14} /> {BOUTONS.telechargerPdf}
                      </Button>
                    )}
                    {isMoe && (!detail.pv || detail.pv.statut === 'genere') && (
                      <Button size="sm" className="rounded-none bg-[#2C2C2C] hover:bg-[#4A4A4A]" onClick={() => setPvOpen(true)} data-testid="reception-pv-generer">
                        {detail.pv ? 'Régénérer le PV' : 'Générer le PV'}
                      </Button>
                    )}
                    {isMoe && detail.pv && ['genere', 'en_signature'].includes(detail.pv.statut) && (
                      <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => setPapierOpen(true)} data-testid="reception-pv-papier">
                        <Signature size={14} /> PV signé sur papier
                      </Button>
                    )}
                  </div>
                </div>
                {!detail.pv && (
                  <p className="text-sm text-[#6B6B6B]">
                    {isMoe ? 'Une fois les réserves saisies, générez le PV (avec ou sans réserves), puis faites-le signer : par code, ou sur place sur papier (joignez alors le scan).' : 'Le PV de réception n\'est pas encore établi.'}
                  </p>
                )}
                {detail.pv && (
                  <p className="text-sm text-[#6B6B6B]">
                    {detail.pv.avec_reserves ? `Réception avec ${detail.pv.nb_reserves} réserve(s)` : 'Réception sans réserve'}
                    {' · '}date d'effet : {dateFr(detail.pv.date_effet)}
                    {detail.pv.statut === 'signe' && detail.pv.signataires?.length > 0 && (
                      <> · signé {detail.pv.mode_signature === 'papier' ? 'sur papier ' : ''}par {detail.pv.signataires.map((s) => s.nom).join(', ')} le {dateFr(detail.pv.signe_le)}</>
                    )}
                    {detail.pv.statut === 'signe' && detail.pv.document_id && <> · rangé dans Documents (PV de réception)</>}
                  </p>
                )}
                {isMoe && detail.pv && detail.pv_a_jour === false && detail.pv.statut === 'genere' && (
                  <p className="text-xs text-[#8B3A3A]">Des réserves ont changé depuis la génération du PV : régénérez-le avant de le faire signer.</p>
                )}
                {isClient && detail.pv && detail.pv.statut !== 'signe' && (
                  <p className="text-xs text-[#6B6B6B]">
                    Vous recevez le lien de signature par e-mail. {FONCTIONS.signatureSms}.
                  </p>
                )}
                {isMoe && detail.pv && detail.pv.statut !== 'remplace' && detail.pv.mode_signature !== 'papier' && (
                  <SignatureManager
                    chantierId={chantierId}
                    documentType="pv_reception"
                    documentId={detail.pv.id}
                    documentTitle={OBJETS.pvReception}
                    defaultSignataires={signatairesProposes}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Après la réception : GPA, DOE / retenue / DGD, clôture */}
      <DemandesGPA chantierId={chantierId} isMoe={isMoe} isClient={isClient} onDecisionLevee={decisionLevee} refreshKey={refreshKey} />
      {isMoe && <ApresReception chantierId={chantierId} refreshKey={refreshKey} />}
      <ClotureChantier chantierId={chantierId} isMoe={isMoe} refreshKey={refreshKey} />

      {ouvrirOpen && (
        <OuvrirReceptionDialog chantierId={chantierId} receptions={receptions} onClose={() => setOuvrirOpen(false)}
          onCreated={(rec) => { setOuvrirOpen(false); setSelId(rec.id); charger(); }} />
      )}
      {reserveOpen && detail && (
        <ReserveDialog chantierId={chantierId} reception={detail} onClose={() => setReserveOpen(false)}
          onCreated={() => { setReserveOpen(false); rafraichir(); }} />
      )}
      {pvOpen && detail && (
        <GenererPvDialog chantierId={chantierId} reception={detail} onClose={() => setPvOpen(false)}
          onDone={() => { setPvOpen(false); rafraichir(); }} />
      )}
      {papierOpen && detail && (
        <PvPapierDialog chantierId={chantierId} reception={detail} signataires={signatairesProposes}
          onClose={() => setPapierOpen(false)} onDone={() => { setPapierOpen(false); rafraichir(); }} />
      )}
      {refus && (
        <RefusLeveeDialog chantierId={chantierId} item={refus.item} onClose={() => setRefus(null)}
          onDone={() => { const apres = refus.apres; setRefus(null); if (apres) apres(); else rafraichir(); }} />
      )}
    </div>
  );
}

function OuvrirReceptionDialog({ chantierId, receptions, onClose, onCreated }) {
  const [lots, setLots] = useState([]);
  const [choisis, setChoisis] = useState([]);
  const [dateReception, setDateReception] = useState(aujourdHui());
  const [proposes, setProposes] = useState([]);
  const [coches, setCoches] = useState([]);
  const [autres, setAutres] = useState([{ nom: '', qualite: '' }]);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true })
      .then(({ data }) => { setLots(data || []); setChoisis((data || []).map((l) => l.id)); })
      .catch(() => setLots([]));
    axios.get(`${API_URL}/api/chantiers/${chantierId}/receptions/presents-proposes`, { withCredentials: true })
      .then(({ data }) => setProposes(data || []))
      .catch(() => setProposes([]));
  }, [chantierId]);

  const basculer = (id) => setChoisis((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const basculerPresent = (i) => setCoches((c) => (c.includes(i) ? c.filter((x) => x !== i) : [...c, i]));

  // P5#15 — lots cochés déjà présents dans une autre réception
  const dejaReceptionnes = useMemo(() => {
    const codes = lots.filter((l) => choisis.includes(l.id)).map((l) => l.code);
    const out = [];
    (receptions || []).forEach((r) => (r.lots || []).forEach((l) => {
      if (codes.includes(l.code)) out.push({ code: l.code, date: r.date_reception, signe: r.pv?.statut === 'signe' });
    }));
    return out;
  }, [lots, choisis, receptions]);

  const valider = async () => {
    setEnvoi(true);
    try {
      const presents = [
        ...coches.map((i) => proposes[i]).filter(Boolean).map((p) => ({ nom: p.nom, qualite: p.qualite })),
        ...autres.filter((a) => a.nom.trim()).map((a) => ({ nom: a.nom.trim(), qualite: a.qualite.trim() })),
      ];
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/receptions`, {
        date_reception: dateReception, lots: choisis, presents,
      }, { withCredentials: true });
      toast.success(`${TITRE_VISITE} ouverte`);
      (data.avertissements || []).forEach((a) => toast.warning(a, { duration: 8000 }));
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setEnvoi(false);
    }
  };

  const groupes = [
    { cle: 'equipe', titre: 'Équipe' },
    { cle: 'client', titre: "Maître d'ouvrage" },
    { cle: 'entreprise', titre: 'Entreprises des lots' },
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="reception-ouvrir-dialog">
        <DialogHeader><DialogTitle>Nouvelle visite de réception (OPR)</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Date de la visite de réception</Label>
            <Input type="date" value={dateReception} onChange={(e) => setDateReception(e.target.value)} className="rounded-none" />
          </div>
          <div>
            <Label>Lots concernés</Label>
            {lots.length === 0 && <p className="text-xs text-[#6B6B6B]">Aucun lot dans l'onglet Marchés.</p>}
            <div className="max-h-48 overflow-y-auto border border-[#E0E0DB] divide-y divide-[#F0F0EB]">
              {lots.map((l) => (
                <label key={l.id} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={choisis.includes(l.id)} onChange={() => basculer(l.id)} />
                  <span>{l.code} — {l.nom}</span>
                  <span className="text-xs text-[#6B6B6B] ml-auto">{l.entreprise_nom}</span>
                </label>
              ))}
            </div>
            {dejaReceptionnes.length > 0 && (
              <p className="text-xs text-[#8B5A00] mt-1 flex items-start gap-1" data-testid="reception-ouvrir-alerte">
                <Warning size={14} className="shrink-0 mt-0.5" />
                {dejaReceptionnes.map((d) => `Le lot ${d.code} figure déjà dans la réception du ${dateFr(d.date)}${d.signe ? ' (PV signé)' : ''}`).join(' · ')}.
              </p>
            )}
          </div>
          <div>
            <Label>Présents</Label>
            <div className="border border-[#E0E0DB] max-h-56 overflow-y-auto">
              {groupes.map((g) => {
                const items = proposes.map((p, i) => ({ ...p, i })).filter((p) => p.groupe === g.cle);
                if (!items.length) return null;
                return (
                  <div key={g.cle}>
                    <p className="px-2 pt-2 text-xs text-[#6B6B6B]">{g.titre}</p>
                    {items.map((p) => (
                      <label key={p.i} className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer" data-testid={`present-${p.i}`}>
                        <input type="checkbox" checked={coches.includes(p.i)} onChange={() => basculerPresent(p.i)} />
                        <span>{p.nom}</span>
                        <span className="text-xs text-[#6B6B6B] ml-auto text-right">{p.qualite}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="space-y-1 mt-2">
              {autres.map((a, i) => (
                <div key={i} className="grid grid-cols-2 gap-1">
                  <Input value={a.nom} placeholder="Autre présent (nom)" className="rounded-none h-8 text-sm"
                    onChange={(e) => setAutres((x) => x.map((y, j) => (j === i ? { ...y, nom: e.target.value } : y)))} />
                  <Input value={a.qualite} placeholder="Qualité" className="rounded-none h-8 text-sm"
                    onChange={(e) => setAutres((x) => x.map((y, j) => (j === i ? { ...y, qualite: e.target.value } : y)))} />
                </div>
              ))}
              <button type="button" className="text-xs underline text-[#1E3A5F]" onClick={() => setAutres((x) => [...x, { nom: '', qualite: '' }])}>
                + Ajouter un autre présent
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
            <Button className="rounded-none bg-[#C4773B] hover:bg-[#A85F25]" disabled={envoi || choisis.length === 0} onClick={valider} data-testid="reception-ouvrir-valider">
              Ouvrir la visite de réception
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReserveDialog({ chantierId, reception, onClose, onCreated }) {
  const [lot, setLot] = useState(reception.lots?.[0]?.code || '');
  const [description, setDescription] = useState('');
  const [localisation, setLocalisation] = useState('');
  const [echeance, setEcheance] = useState('');
  const [photo, setPhoto] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const entreprise = (reception.lots || []).find((l) => l.code === lot)?.entreprise_nom;

  const valider = async () => {
    setEnvoi(true);
    try {
      let photoUrl = null;
      if (photo) {
        const fd = new FormData();
        fd.append('file', photo);
        fd.append('doc_type', 'photo');
        // P5#8 — une photo déjà envoyée n'est plus perdue en silence
        fd.append('force_duplicate', 'true');
        fd.append('visible_to', JSON.stringify(['archi', 'moe_principal', 'moe_membre', 'consultant', 'entreprise', 'client']));
        fd.append('notes', `Réserve de réception — lot ${lot}`);
        const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/upload`, fd, { withCredentials: true });
        photoUrl = data?.url || data?.media_url || null;
        if (!photoUrl) throw new Error("La photo n'a pas pu être envoyée : réessayez.");
      }
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/receptions/${reception.id}/reserves`, {
        lot, description, localisation, echeance: echeance || null, photo_url: photoUrl,
      }, { withCredentials: true });
      toast.success('Réserve ajoutée');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Erreur');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="reception-reserve-dialog">
        <DialogHeader><DialogTitle>Ajouter une réserve</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Lot</Label>
            <select value={lot} onChange={(e) => setLot(e.target.value)} className="w-full border border-[#E0E0DB] p-2 text-sm">
              {(reception.lots || []).map((l) => <option key={l.code} value={l.code}>{l.code} — {l.nom}</option>)}
            </select>
            {entreprise && <p className="text-xs text-[#6B6B6B] mt-1">Entreprise : {entreprise}</p>}
          </div>
          <div>
            <Label>Réserve</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-none" />
          </div>
          <div>
            <Label>Localisation (pièce, niveau…)</Label>
            <Input value={localisation} onChange={(e) => setLocalisation(e.target.value)} className="rounded-none" />
          </div>
          <div>
            <Label>À lever avant le (90 jours par défaut)</Label>
            <Input type="date" value={echeance} onChange={(e) => setEcheance(e.target.value)} className="rounded-none" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Camera size={14} /> Photo</Label>
            <Input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] || null)} className="rounded-none" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
            <Button className="rounded-none bg-[#C4773B] hover:bg-[#A85F25]" disabled={envoi || description.trim().length < 3} onClick={valider}>
              Ajouter
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// P5#12 — date d'effet dans un vrai formulaire avec calendrier (plus de fenêtre du navigateur)
function GenererPvDialog({ chantierId, reception, onClose, onDone }) {
  const [dateEffet, setDateEffet] = useState(reception.pv?.date_effet || reception.date_reception || aujourdHui());
  const [observations, setObservations] = useState(reception.observations || '');
  const [envoi, setEnvoi] = useState(false);

  const valider = async () => {
    setEnvoi(true);
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/receptions/${reception.id}/pv`,
        { date_effet: dateEffet || null, observations }, { withCredentials: true });
      toast.success(`${OBJETS.pvReception} généré`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="reception-pv-dialog">
        <DialogHeader><DialogTitle>{reception.pv ? 'Régénérer le PV' : 'Générer le PV de réception'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Date d'effet de la réception</Label>
            <Input type="date" value={dateEffet} onChange={(e) => setDateEffet(e.target.value)} className="rounded-none" data-testid="reception-pv-date" />
            <p className="text-xs text-[#6B6B6B] mt-1">La garantie de parfait achèvement court un an à partir de cette date.</p>
          </div>
          <div>
            <Label>Observations (facultatif)</Label>
            <Textarea rows={3} value={observations} onChange={(e) => setObservations(e.target.value)} className="rounded-none" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
            <Button className="rounded-none bg-[#2C2C2C] hover:bg-[#4A4A4A]" disabled={envoi || !dateEffet} onClick={valider} data-testid="reception-pv-valider">
              Générer le PV
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// P5#10 / B15 — PV signé sur place : date + scan joint
function PvPapierDialog({ chantierId, reception, signataires, onClose, onDone }) {
  const [dateSignature, setDateSignature] = useState(aujourdHui());
  const [noms, setNoms] = useState(signataires.map((s) => s.nom).filter(Boolean).join('\n'));
  const [scan, setScan] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const valider = async () => {
    setEnvoi(true);
    try {
      const fd = new FormData();
      fd.append('date_signature', dateSignature);
      fd.append('signataires', noms);
      fd.append('file', scan);
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/receptions/${reception.id}/pv/papier`, fd, { withCredentials: true });
      toast.success('PV enregistré comme signé : lots réceptionnés, PV rangé dans Documents');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="reception-papier-dialog">
        <DialogHeader><DialogTitle>PV signé sur papier</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#6B6B6B]">Le PV a été signé sur place ? Indiquez la date et joignez le scan (ou une photo) du PV signé.
            {reception.pv?.statut === 'en_signature' ? ' La signature électronique en cours sera annulée.' : ''}</p>
          <div>
            <Label>Date de signature</Label>
            <Input type="date" max={aujourdHui()} value={dateSignature} onChange={(e) => setDateSignature(e.target.value)} className="rounded-none" data-testid="papier-date" />
          </div>
          <div>
            <Label>Signataires (un par ligne)</Label>
            <Textarea rows={3} value={noms} onChange={(e) => setNoms(e.target.value)} className="rounded-none" />
          </div>
          <div>
            <Label>Scan du PV signé (PDF ou photo)</Label>
            <Input type="file" accept="application/pdf,image/*" onChange={(e) => setScan(e.target.files?.[0] || null)} className="rounded-none" data-testid="papier-scan" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
            <Button className="rounded-none bg-[#2D6A4F] hover:bg-[#1B4332]" disabled={envoi || !scan || !dateSignature} onClick={valider} data-testid="papier-valider">
              Enregistrer le PV signé
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// P5#12 — motif du refus d'une levée dans un vrai formulaire
function RefusLeveeDialog({ chantierId, item, onClose, onDone }) {
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const valider = async () => {
    setEnvoi(true);
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/elements/${item.id}/refuser-levee`, { motif: motif.trim() }, { withCredentials: true });
      toast.success('Levée refusée : l\'entreprise est prévenue');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="reception-refus-dialog">
        <DialogHeader><DialogTitle>{BOUTONS.refuserLevee}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#2C2C2C]">{item.description}</p>
          <div>
            <Label>Motif du refus (transmis à l'entreprise)</Label>
            <Textarea rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} className="rounded-none" data-testid="refus-motif" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
            <Button className="rounded-none bg-[#8B3A3A] hover:bg-[#6E2E2E]" disabled={envoi || !motif.trim()} onClick={valider} data-testid="refus-valider">
              Refuser la levée
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
